/**
 * One customer: an enterprise or an individual.
 *
 * Opened by a platform role, by the business itself, or by a provider holding a
 * live application and a consent grant. Which of those is reading decides what
 * appears, and that decision is taken in SQL before this screen is reached: the
 * row scope on the grant is a predicate, and a provider opening a customer it
 * has no application for receives a refusal rather than a thinner page.
 *
 * The disclosure panel is the one worth reading twice. It reports what has
 * actually been read about this business and by whom, including refusals, which
 * is the only form of a consent record a person can check rather than trust.
 */
import { Link } from 'react-router-dom'

import type { Dict, SectionMeta } from '../../api/types'
import { useSession } from '../../app/session'
import { appPath } from '../../app/paths'
import { AttributeTable } from '../../components/Attributes'
import type { Provenance } from '../../components/Attributes'
import { Badge, Empty, KV, Meter, Note, toneOf } from '../../components/bits'
import { PageHead } from '../../components/PageHead'
import { Panel, Cols } from '../../components/Panel'

export function CustomerRecord({ data }: { data: Dict }) {
  const { n, amount, d, dt, lbl } = useSession()
  const c = data.c as Dict
  const section = data.section as SectionMeta
  const people = (data.people || []) as Dict[]
  const requests = (data.requests || []) as Dict[]
  const grants = (data.grants || []) as Dict[]
  const docs = (data.docs || []) as Dict[]
  const assessment = data.assessment as Dict | null
  const completeness = data.completeness as Dict | null
  const disclosure = data.disclosure as Dict | null

  return (
    <>
      <PageHead section={section} title={c.display_name_en as string}
                subtitle={c.customer_ref as string}
                auditLink={data.audit_link as string} />

      <Cols>
        <Panel title="Who this is" explain="customer.record">
          <KV rows={[
            ['Reference', <code key="r">{c.customer_ref as string}</code>],
            ['Kind', <Badge tone="muted" key="k">{lbl(c.customer_type)}</Badge>],
            ['Segment', c.segment ? lbl(c.segment) : '—'],
            ['Registry number', c.cr_number
              ? <code key="cr">{c.cr_number as string}</code> : '—'],
            ['Registry status', c.cr_status
              ? <Badge tone={toneOf(c.cr_status)} key="cs">{lbl(c.cr_status)}</Badge>
              : '—'],
            ['Registry expires', c.cr_expiry_date ? d(c.cr_expiry_date) : '—'],
            ['Legal form', c.legal_form ? lbl(c.legal_form) : '—'],
            ['Incorporated', c.incorporation_date ? d(c.incorporation_date) : '—'],
            ['Size', c.size_classification
              ? <Badge tone="muted" key="sz">{lbl(c.size_classification)}</Badge> : '—'],
            ['Paid capital', c.paid_capital ? amount(c.paid_capital) : '—'],
            ['Headquarters', [c.hq_city, c.hq_region].filter(Boolean).join(', ') || '—'],
            ['Identity', <Badge tone={toneOf(c.kyc_status)} key="kyc">
              {lbl(c.kyc_status)}</Badge>],
            ['Onboarding', <Badge tone={toneOf(c.onboarding_state)} key="ob">
              {lbl(c.onboarding_state)}</Badge>],
            ['Accepts', [
              c.accepts_traditional ? 'conventional' : null,
              c.accepts_sharia ? 'Sharia compliant' : null,
            ].filter(Boolean).join(' and ') || 'unstated'],
            ...(c.compliance_preference
              ? [['Stated preference', lbl(c.compliance_preference)] as [string, string]]
              : []),
          ]} />
          {Boolean(c.display_name_ar) && (
            <p className="ar" dir="rtl">{c.display_name_ar as string}</p>
          )}
        </Panel>

        <div>
          <Panel title="Profile completeness" explain="profile.completeness">
            {completeness ? (
              <>
                <div className="big-value">
                  <b className="num">{n(completeness.completeness_pct, 0)}%</b>
                  <span className="muted">
                    of which {n(completeness.verified_pct, 0)}% verified
                  </span>
                </div>
                <Meter pct={Number(completeness.completeness_pct)} />
                {Array.isArray(completeness.blocking_keys)
                  && (completeness.blocking_keys as string[]).length > 0 && (
                  <div className="cav">
                    <b>Blocking an assessment.</b>{' '}
                    {(completeness.blocking_keys as string[]).map((k) => (
                      <code key={k} style={{ marginInlineEnd: 5 }}>{k}</code>
                    ))}
                  </div>
                )}
              </>
            ) : <Empty title="No profile has been assembled" />}
          </Panel>

          <Panel title="Readiness" explain="readiness.assessment">
            {assessment ? (
              <>
                <div className="big-value">
                  <b className="num">{n(assessment.score, 1)}</b>
                  <span className="muted">{lbl(assessment.band)}</span>
                </div>
                <p className="xs muted">
                  Model {String(assessment.model_version)}, computed{' '}
                  {dt(assessment.computed_at)}.
                </p>
                <Note kind="warn">
                  <b>A model output, not a credit decision.</b> No provider sees
                  it, and it is calibrated on fewer real outcomes than it needs
                  to carry weight.
                </Note>
              </>
            ) : <Empty title="Not assessed" />}
          </Panel>
        </div>
      </Cols>

      <Panel title="What is held about this business" explain="profile.attributes" tight>
        <AttributeTable attrs={(data.attrs || {}) as Record<string, unknown>}
                        provenance={(data.provenance || {}) as Record<string, Provenance>}
                        stale={(data.stale || []) as string[]}
                        withheld={(data.withheld || []) as string[]} />
      </Panel>

      <Cols>
        <Panel title={`Consent grants · ${grants.length}`} explain="consent.my_grants" tight>
          {grants.length === 0 ? (
            <Empty title="Nothing is granted">
              No consumer can read anything about this business.
            </Empty>
          ) : (
            <div className="tbl-wrap">
              <table className="dl">
                <thead>
                  <tr><th>Consumer</th><th>Purpose</th><th>Basis</th>
                    <th>Until</th><th className="acts" /></tr>
                </thead>
                <tbody>
                  {grants.map((g) => (
                    <tr key={String(g.consent_id)}>
                      <td data-label="Consumer">{g.consumer_name as string}</td>
                      <td data-label="Purpose">{lbl(g.purpose_code)}</td>
                      <td data-label="Basis">
                        <Badge tone="muted">{lbl(g.legal_basis)}</Badge>
                      </td>
                      <td data-label="Until">
                        {g.expires_at ? d(g.expires_at) : <span className="muted">open</span>}
                      </td>
                      <td className="acts">
                        <Link className="btn sm"
                              to={appPath(`/app/consents/${g.consent_id}`)}>Open</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="xs muted">
            A grant names its consumer, its purpose and its scope set. There is no
            wildcard scope and no grant to a category of consumer, because a
            grant nobody can enumerate is a grant nobody can revoke.
          </p>
        </Panel>

        <Panel title="What has been read" explain="consent.disclosures">
          {disclosure ? (
            <>
              <div className="big-value">
                <b className="num">{n(disclosure.total)}</b>
                <span className="muted">disclosures</span>
              </div>
              <KV rows={[
                ['Refused by the gateway', n(disclosure.denied)],
                ['Last read', disclosure.last_access
                  ? dt(disclosure.last_access) : 'never'],
              ]} />
              <p className="xs muted">
                Refusals are recorded alongside disclosures. A register of
                successful reads could not answer whether the gateway ever said
                no, which is the question a business actually has.
              </p>
            </>
          ) : <Empty title="Nothing has been read" />}
        </Panel>
      </Cols>

      <Panel title={`Requests · ${requests.length}`} explain="funding.my_requests" tight>
        {requests.length === 0 ? <Empty title="No request has been raised" /> : (
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr>
                  <th>Reference</th><th className="num">Amount</th><th>Purpose</th>
                  <th>State</th><th>Raised</th><th className="acts" />
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={String(r.request_id)}>
                    <td data-label="Reference">
                      <span className="mono clip">{r.request_ref as string}</span>
                    </td>
                    <td data-label="Amount" className="num">
                      {amount(r.amount_requested, r.currency as string)}
                    </td>
                    <td data-label="Purpose">{lbl(r.economic_purpose)}</td>
                    <td data-label="State">
                      <Badge tone={toneOf(r.status)}>{lbl(r.status)}</Badge>
                    </td>
                    <td data-label="Raised">{d(r.created_at)}</td>
                    <td className="acts">
                      <Link className="btn sm"
                            to={appPath(`/app/requests/${r.request_id}`)}>Open</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {people.length > 0 && (
        <Panel title={`People · ${people.length}`} explain="customer.record" tight>
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr><th>Name</th><th>Role</th><th>Identity verified</th></tr>
              </thead>
              <tbody>
                {people.map((p, i) => (
                  <tr key={i}>
                    <td data-label="Name">{(p.full_name_en || p.display_name) as string}</td>
                    <td data-label="Role">{lbl(p.relationship || p.job_title)}</td>
                    <td data-label="Identity verified">
                      {p.nafath_verified_at
                        ? <Badge tone="good">{d(p.nafath_verified_at)}</Badge>
                        : <Badge tone="warn">not verified</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {docs.length > 0 && (
        <Panel title={`Documents · ${docs.length}`} explain="customer.record" tight>
          <div className="tbl-wrap">
            <table className="dl">
              <thead>
                <tr><th>Document</th><th>Kind</th><th>State</th><th>Loaded</th></tr>
              </thead>
              <tbody>
                {docs.map((doc, i) => (
                  <tr key={i}>
                    <td data-label="Document">{(doc.file_name || doc.title) as string}</td>
                    <td data-label="Kind">{lbl(doc.document_type)}</td>
                    <td data-label="State">
                      <Badge tone={toneOf(doc.status)}>{lbl(doc.status)}</Badge>
                    </td>
                    <td data-label="Loaded">{doc.created_at ? dt(doc.created_at) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="xs muted">
            Documents are recorded and not read by the platform. Extraction is not
            implemented, so a figure on a statement is not an attribute until
            somebody reviews it and the review is recorded as its provenance.
          </p>
        </Panel>
      )}
    </>
  )
}
